import { IntegrationDefinition, IntegrationConnection } from '@/types/integrations';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { format } from 'date-fns';
import { ScrollArea } from '@/components/ui/scroll-area';

interface IntegrationSettingsDialogProps {
  integration: IntegrationDefinition | null;
  connection: IntegrationConnection | null;
  open: boolean;
  onClose: () => void;
  onReconnect: (integration: IntegrationDefinition) => void;
  onDisconnect: (connection: IntegrationConnection) => void;
}

export function IntegrationSettingsDialog({
  integration,
  connection,
  open,
  onClose,
  onReconnect,
  onDisconnect,
}: IntegrationSettingsDialogProps) {
  const [showLogs, setShowLogs] = useState(false);

  // Mock sync logs
  const syncLogs = [
    { timestamp: new Date().toISOString(), status: 'success', message: 'Synced 150 customers' },
    { timestamp: new Date(Date.now() - 3600000).toISOString(), status: 'success', message: 'Synced 45 invoices' },
    { timestamp: new Date(Date.now() - 7200000).toISOString(), status: 'success', message: 'Synced 23 payments' },
  ];

  if (!integration || !connection) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="text-3xl">{integration.logo}</div>
            <div>
              <DialogTitle>{integration.name} Settings</DialogTitle>
              <DialogDescription>Manage your integration connection</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Connection Status */}
          <div className="space-y-2">
            <div className="text-sm font-medium">Connection Status</div>
            <div className="flex items-center gap-2">
              {connection.status === 'connected' ? (
                <Badge variant="outline" className="border-green-500 text-green-700 bg-green-50">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              ) : (
                <Badge variant="outline" className="border-red-500 text-red-700 bg-red-50">
                  <XCircle className="h-3 w-3 mr-1" />
                  Error
                </Badge>
              )}
            </div>
          </div>

          <Separator />

          {/* Connected Account */}
          {connection.connectedAccount && (
            <>
              <div className="space-y-2">
                <div className="text-sm font-medium">Connected Account</div>
                <div className="text-sm text-muted-foreground">{connection.connectedAccount}</div>
              </div>
              <Separator />
            </>
          )}

          {/* Credentials (masked) */}
          <div className="space-y-2">
            <div className="text-sm font-medium">Credentials</div>
            <div className="space-y-1">
              {Object.keys(connection.credentials).map((key) => (
                <div key={key} className="flex justify-between text-sm">
                  <span className="text-muted-foreground capitalize">{key}:</span>
                  <span className="font-mono">••••••••</span>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Last Sync */}
          {connection.lastSync && (
            <>
              <div className="space-y-2">
                <div className="text-sm font-medium">Last Sync</div>
                <div className="text-sm text-muted-foreground">
                  {format(new Date(connection.lastSync), 'PPpp')}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Sync Logs */}
          <div className="space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-between p-0 h-auto font-medium text-sm"
              onClick={() => setShowLogs(!showLogs)}
            >
              Sync Logs
              {showLogs ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            {showLogs && (
              <ScrollArea className="h-[150px] border rounded-md p-3">
                <div className="space-y-2">
                  {syncLogs.map((log, index) => (
                    <div key={index} className="text-xs space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {format(new Date(log.timestamp), 'PPp')}
                        </span>
                        <Badge
                          variant="outline"
                          className="border-green-500 text-green-700 bg-green-50 text-xs"
                        >
                          {log.status}
                        </Badge>
                      </div>
                      <div>{log.message}</div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Advanced Settings */}
          {connection.config && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="text-sm font-medium">Advanced Settings</div>
                <div className="text-xs text-muted-foreground">
                  Configuration options are available for this integration
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onDisconnect(connection)}
          >
            Disconnect
          </Button>
          <Button
            variant="outline"
            onClick={() => onReconnect(integration)}
          >
            Reconnect
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
